import React, {
  CSSProperties,
  Dispatch,
  forwardRef,
  KeyboardEvent,
  MutableRefObject,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import cn from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectSavedSelectedContactForView,
  selectSelectedContactBatch,
  setContactBatch,
} from '@repo/redux/actions/ui';
import Header from '@components/Header';
import Container from '@components/ContentContainer';
import { createListContext, List as VirtualizedList } from '@components/List/VirtualizedList';
import Sorting from '../Sorting';
import { SearchTips } from '../Tips';
import SearchResult from '../SearchResult';
import Contact from '@repo/utils/models/Contact';
import { useLocation, useNavigate } from 'react-router-dom';
import { IDRecord, SearchState, UserAction } from '@repo/utils/types';
import styles from './ResultsDisplay.module.scss';
import analytics from '@repo/utils/helpers/analytics';
import { ContactPreviewSidebar } from '@components/Contact/Sidebar';
import { useWorkspaceContext } from '@repo/hooks';
import searchResultStyles from '../SearchResult/SearchResult.module.scss';
import BaseObject from '@repo/utils/models/BaseObject';
import { selectNavBarState } from '@repo/redux/reducers/ui/navBar';
import RecentSearches from '../RecentSearches';

const SearchListContext = createListContext();

const renderSearchResult = (contact: BaseObject) => (
  <SearchResult result={contact} largeContactName isMainSearchView />
);
const renderPersonResult = (contact: BaseObject) => (
  <SearchResult result={contact} largeContactName showSearchMatch={false} />
);

interface SharedProps {
  search: SearchState;
  setHighlightedItem: Dispatch<Contact>;
  defaultHighlightedItem?: Contact | null;
  defaultHighlightedIdx?: number;
  headerIcons?: ReactNode;
  headerIconsOnRight?: boolean;
  dropdownMenuOptions?: Array<UserAction>;
  disabled?: boolean;
  onHotKey?: (item: IDRecord, event: KeyboardEvent) => void;
  emptyStateUI?: ReactNode;
  beforeHeaderActions?: ReactNode;
  listHeader?: ReactNode;
}

interface ResultsListProps extends SharedProps {
  loadNextPage: () => void;
  headerRef: MutableRefObject<HTMLDivElement>;
  resetListHighlightOnSearch?: boolean;
}

const ResultsList = forwardRef(
  (
    {
      search,
      loadNextPage,
      setHighlightedItem,
      headerRef,
      defaultHighlightedItem,
      defaultHighlightedIdx,
      resetListHighlightOnSearch,
      headerIcons,
      headerIconsOnRight,
      dropdownMenuOptions,
      disabled,
      onHotKey,
      emptyStateUI,
      beforeHeaderActions,
      listHeader,
    }: ResultsListProps,
    ref: MutableRefObject<HTMLDivElement>,
  ) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { isMultiPlayer } = useWorkspaceContext();
    const lastSearchUpdateTime = useMemo(() => search.results.updated, []);
    const selectedContactBatch = useSelector(selectSelectedContactBatch);
    let results = search.results.filteredItems.valueSeq().toArray();

    if (listHeader) {
      // @ts-ignore
      results = [{ id: 'sectionHeader', label: listHeader }, ...results];
    }

    const navigateToContact = ({ id }: IDRecord) => {
      analytics.track('Search Result Tapped');
      navigate(`/contact/${id}`);
    };

    let renderItem = renderPersonResult;
    if (search.type == 'search') {
      renderItem = renderSearchResult;
    }

    const _defaultHighlightedIdx = useMemo(() => {
      if (
        defaultHighlightedItem &&
        lastSearchUpdateTime &&
        lastSearchUpdateTime.isEqual(search.results.updated)
      ) {
        const highlightIdx = results.findIndex(({ id }) => id == defaultHighlightedItem.id);
        return highlightIdx;
      }
      return defaultHighlightedIdx || 0;
    }, [search.results.updated]);

    const headerContent = (
      <>
        {!headerIconsOnRight && (headerIcons || null)}
        <Sorting searchType={search.type} resourceID={search.resourceID} />
        {headerIconsOnRight && (headerIcons || null)}
      </>
    );

    const forceHighlightResetRef = useRef('false');

    useEffect(() => {
      if (resetListHighlightOnSearch && search.results.page == 1) {
        forceHighlightResetRef.current = search.results.updated
          ? search.results.updated.toString()
          : 'false';
      }
    }, [search.results.updated]);

    return (
      <VirtualizedList
        multiselect
        shouldResetHighlight={search.results.page == 0}
        items={results as BaseObject[]}
        renderItem={renderItem}
        // @ts-ignore
        onItemClick={navigateToContact}
        // @ts-ignore
        onHotKey={onHotKey}
        canLoadMore={search.pauseLoadingMore ? false : search.results.canLoadMore}
        loadMore={loadNextPage}
        selectedItems={selectedContactBatch}
        setSelectedItems={(contactIDs) => dispatch(setContactBatch(contactIDs, 'search-view'))}
        determineItemSize={(item) => (item.id == 'sectionHeader' ? 36 : 48)}
        headerRef={headerRef}
        ListContext={SearchListContext}
        estimatedCount={search.results.totalResults}
        onItemHighlight={setHighlightedItem}
        headerCutoff={1}
        headerContent={search.term.length > 0 && headerContent}
        dropdownMenuOptions={dropdownMenuOptions}
        beforeHeaderActions={beforeHeaderActions}
        emptyListView={emptyStateUI}
        draggableItems
        ref={ref}
        defaultHighlightedIdx={_defaultHighlightedIdx}
        forceResetHighlightedIdx={forceHighlightResetRef.current}
        listRowClassName={
          ['allPeopleSearch', 'groupSearch'].includes(search.type) && !isMultiPlayer
            ? searchResultStyles.noCenterColumn
            : undefined
        }
        disabled={disabled}
        hasListHeader={!!listHeader}
      />
    );
  },
);

// const SearchPaywallBanner = () => (
//   <PaywallBanner className={styles.paywallBanner} href="https://go.clay.earth/upgrade">
//     Some older search results may not be shown.
//   </PaywallBanner>
// );

interface SearchResultsDisplayProps extends SharedProps {
  highlightedItem?: Contact;
  loadNextPage: () => void;
  title: ReactNode;
  resetListHighlightOnSearch?: boolean;
  // noSearchTermStateUI?: ReactNode;
  headerStyle?: CSSProperties;
  sidebar?: ReactNode;
  afterList?: ReactNode;
}

function ResultsDisplay(
  {
    search,
    defaultHighlightedItem,
    defaultHighlightedIdx,
    highlightedItem,
    setHighlightedItem,
    loadNextPage,
    title,
    resetListHighlightOnSearch,
    headerIcons,
    headerIconsOnRight,
    dropdownMenuOptions,
    emptyStateUI = null,
    headerStyle = {},
    sidebar,
    afterList,
    beforeHeaderActions,
    listHeader,
    disabled,
    onHotKey,
  }: SearchResultsDisplayProps,
  ref: MutableRefObject<HTMLDivElement>,
) {
  const location = useLocation();
  const navigate = useNavigate();
  const userIsSearching = search.term.length > 0;
  const isOnGroupView = location.pathname.includes('group');
  const headerRef = useRef(null);
  const { status } = useSelector(selectNavBarState);
  const savedSelectedContactForView = useSelector(
    selectSavedSelectedContactForView(location.pathname),
  );

  const onGoBack = useCallback(
    (triggeredByClick?: boolean) => {
      if (triggeredByClick) {
        navigate(-1);
      }
    },
    [navigate],
  );

  const renderDisplay = () => {
    if (!userIsSearching) {
      return <SearchTips />;
    }

    let _emptyStateUI = null;
    if (userIsSearching && !search.results.isFetching && search.results.totalResults == 0) {
      _emptyStateUI = emptyStateUI;
    }

    return (
      <ResultsList
        search={search}
        loadNextPage={loadNextPage}
        defaultHighlightedItem={savedSelectedContactForView || defaultHighlightedItem}
        defaultHighlightedIdx={defaultHighlightedIdx}
        setHighlightedItem={setHighlightedItem}
        headerRef={headerRef}
        ref={ref}
        resetListHighlightOnSearch={resetListHighlightOnSearch}
        headerIcons={headerIcons}
        headerIconsOnRight={headerIconsOnRight}
        dropdownMenuOptions={dropdownMenuOptions}
        disabled={disabled}
        onHotKey={onHotKey}
        emptyStateUI={_emptyStateUI}
        beforeHeaderActions={beforeHeaderActions}
        listHeader={listHeader}
      />
    );
  };

  return (
    <Container>
      <Container.Primary
        className={styles.searchContainer}
        id="content"
        header={
          <Header
            className={cn(
              styles.header,
              isOnGroupView && styles.groupHeader,
              styles[`navBar-${status}`],
            )}
            onGoBack={onGoBack}
            goBack={location.pathname !== '/people' && !isOnGroupView}
            actions={false}
            style={headerStyle}
          >
            <div className={styles.results_header}>
              {title}
              <div className={styles.results_actions} ref={headerRef} />
            </div>
          </Header>
        }
      >
        {renderDisplay()}
        {/* {search.type == 'search' && !userHasFullSearch && <SearchPaywallBanner />} */}
        {!!afterList && <>{afterList}</>}
      </Container.Primary>
      {!!sidebar ? (
        <Container.Sidebar>{sidebar}</Container.Sidebar>
      ) : (
        <ContactPreviewSidebar
          contactID={
            search.term.length > 0 && highlightedItem?.id !== 'paddingRow' && highlightedItem?.id
          }
        >
          {search.tips && (
            <div className={styles.sidebar}>
              <RecentSearches search={search} />
            </div>
          )}
        </ContactPreviewSidebar>
      )}
    </Container>
  );
}

export default forwardRef(ResultsDisplay);
